import React, {useState, useContext, useCallback, useEffect, useRef} from 'react';
import {db} from '../firebase';
import {useAuth} from './AuthContext';
import {DEFAULT_SEASON} from '../config';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  orderBy,
  setDoc,
} from 'firebase/firestore';
import {getFunctions, httpsCallable} from 'firebase/functions';

const DataContext = React.createContext();

export const useData = () => {
    return useContext(DataContext);
}

export const DataProvider = ( {children} ) => {

    const {currentUser, isApproved} = useAuth();
    // Pool data is only readable by approved members (security rules), so
    // nothing below is fetched for a pending account — it would just log
    // permission errors.
    const member = !!currentUser && isApproved;
    const [winningTeams, setWinningTeams] = useState(null);
    const [currentSeason, setCurrentSeason] = useState(null);
    const [seasonLoading, setSeasonLoading] = useState(true);
    const [userDisplayNames, setUserDisplayNames] = useState(new Map());
    // Bumped to re-run the public-profiles fetch (e.g., after the current
    // user changes their own display name) without a full page reload.
    const [profilesVersion, setProfilesVersion] = useState(0);

    // In-memory team cache — persists across page navigations within a session.
    // Eliminates redundant Firestore reads for team data (the N+1 query problem).
    const teamCache = useRef(new Map());

    // Load display names for all users (for leaderboard/picks display) via the
    // getPublicProfiles Cloud Function, which returns only { email, displayName }.
    // Regular users can no longer read the full users collection (which holds
    // phone numbers), so this projection is fetched server-side. Names change
    // rarely, so a fetch on auth change is sufficient (no live listener needed);
    // refreshUserDisplayNames() re-fetches on demand after a profile update.
    useEffect(() => {
        if (!member) {
            setUserDisplayNames(new Map());
            return;
        }
        let cancelled = false;
        const loadProfiles = async () => {
            try {
                const getPublicProfiles = httpsCallable(getFunctions(), 'getPublicProfiles');
                const result = await getPublicProfiles();
                if (cancelled) return;
                const nameMap = new Map();
                (result.data?.profiles || []).forEach(({email, displayName}) => {
                    if (email && displayName) nameMap.set(email, displayName);
                });
                setUserDisplayNames(nameMap);
            } catch (error) {
                console.error('Error loading public profiles:', error);
            }
        };
        loadProfiles();
        return () => { cancelled = true; };
    }, [member, profilesVersion]);

    // Re-fetch the public display-name map (e.g., after the current user
    // saves a new display name) so the change shows without a reload.
    const refreshUserDisplayNames = useCallback(() => {
        setProfilesVersion(v => v + 1);
    }, []);

    // Fetch the current season from Firestore config when authenticated
    useEffect(() => {
        if (!member) {
            // Allow unauthenticated pages (landing, login) and the pending
            // page to render, but don't set a season — keep it null until
            // the account is an approved member.
            setCurrentSeason(null);
            setSeasonLoading(false);
            return;
        }
        const fetchCurrentSeason = async () => {
            try {
                const configRef = doc(db, 'config', 'app');
                const configSnap = await getDoc(configRef);
                if (configSnap.exists() && configSnap.data().currentSeason) {
                    setCurrentSeason(configSnap.data().currentSeason);
                } else {
                    // First time setup — create the config doc with current year
                    await setDoc(configRef, { currentSeason: DEFAULT_SEASON }, { merge: true });
                    setCurrentSeason(DEFAULT_SEASON);
                }
            } catch (error) {
                console.error('Error fetching current season:', error);
                setCurrentSeason(DEFAULT_SEASON);
            } finally {
                setSeasonLoading(false);
            }
        };
        fetchCurrentSeason();
    }, [member]);

    // Admin function: update the current season
    const setAppSeason = async (season) => {
        const configRef = doc(db, 'config', 'app');
        // Use setDoc with merge to handle both create and update cases
        await setDoc(configRef, { currentSeason: season }, { merge: true });
        setCurrentSeason(season);
    };

    // Admin function: update the current week for a season
    const setCurrentWeek = async (season, week) => {
        const seasonRef = doc(db, 'games', season);
        await setDoc(seasonRef, { currentWeek: week }, { merge: true });
    };
    const UNKNOWN_TEAM = { name: 'Unknown', logo: '', color: '#ccc', fontColor: '#000', conference: '', stadium: '' };

    const getTeamByName = useCallback(async (name) => {
        // Check cache first — avoids redundant Firestore reads
        if (teamCache.current.has(name)) {
            return teamCache.current.get(name);
        }
        try {
            const teamsRef = doc(db, 'teams', name);
            const docSnap = await getDoc(teamsRef);
            if (docSnap.exists()) {
                const team = docSnap.data();
                teamCache.current.set(name, team);
                return team;
            }
        } catch (error) {
            console.error(`Error fetching team "${name}":`, error);
        }
        // Return placeholder for missing/failed teams so consumers don't crash
        const placeholder = { ...UNKNOWN_TEAM, name };
        teamCache.current.set(name, placeholder);
        return placeholder;
    }, []);

    const getGamesForWeek = useCallback(async (season, week) => {
        try {
            const gamesFromDb = [];
            const gamesRef = query(collection(db, 'games', season, week.toString()), orderBy('displayOrder'));
            const snapshot = await getDocs(gamesRef);
            if (!snapshot.empty) {
                snapshot.forEach((doc) => {
                    gamesFromDb.push({ id: doc.id, ...doc.data() });
                });

                // Collect unique team names and fetch them all in parallel
                const teamNames = new Set();
                gamesFromDb.forEach(g => {
                    teamNames.add(g.homeTeam);
                    teamNames.add(g.awayTeam);
                });
                await Promise.all([...teamNames].map(name => getTeamByName(name)));

                // Assign cached team data and check disabled status
                const currentTime = new Date().getTime() / 1000;
                for (let i = 0; i < gamesFromDb.length; i++) {
                    gamesFromDb[i].homeTeam = teamCache.current.get(gamesFromDb[i].homeTeam) || { ...UNKNOWN_TEAM, name: gamesFromDb[i].homeTeam };
                    gamesFromDb[i].awayTeam = teamCache.current.get(gamesFromDb[i].awayTeam) || { ...UNKNOWN_TEAM, name: gamesFromDb[i].awayTeam };
                    const startTime = gamesFromDb[i].startTime;
                    gamesFromDb[i].disabled = startTime && startTime.seconds ? startTime.seconds < currentTime : false;
                }
            }
            return gamesFromDb;
        } catch (error) {
            console.error('Error fetching games for week:', error);
            return [];
        }
    }, [getTeamByName]);

    const getPicksForUser = useCallback(async (season, week) => {
        try {
            const picksRef = doc(db, 'picks', season, week.toString(), currentUser.email);
            const docSnap = await getDoc(picksRef);
            if (docSnap.exists()) {
                return docSnap.data().picks;
            }
        } catch (error) {
            console.error('Error fetching user picks:', error);
        }
        return [];
    }, [currentUser]);

    // Everyone's picks for a week, as [{ name, picks }]. Served by the
    // getWeekPicks Cloud Function: security rules let a member read only
    // their own pick doc, and the function hides other players' picks on
    // games that haven't kicked off (the leaderboard's "Locked" cell used to
    // be the only thing hiding them).
    const getAllPicksForWeek = useCallback(async (season, week) => {
        try {
            const getWeekPicks = httpsCallable(getFunctions(), 'getWeekPicks');
            const result = await getWeekPicks({ season, week });
            return result.data?.picks || [];
        } catch (error) {
            console.error('Error fetching all picks for week:', error);
            return [];
        }
    }, []);

    const getCurrentWeek = useCallback(async (season) => {
        try {
            if (!season) return 1;
            const seasonRef = doc(db, 'games', season);
            const docSnap = await getDoc(seasonRef);
            if (!docSnap.exists()) {
                // Don't auto-create — just return default week
                return 1;
            }
            return docSnap.data().currentWeek;
        } catch (error) {
            console.error('Error fetching current week:', error);
            return 1;
        }
    }, []);

    const getLeaderboard = useCallback(async (season) => {
        try {
            const q = query(
                collection(db, 'leaderboard', season, 'totalPoints'),
                orderBy('totalPoints', 'desc')
            );
            const snapshot = await getDocs(q);
            return snapshot.docs;
        } catch (error) {
            console.error('Error fetching leaderboard:', error);
            return [];
        }
    }, []);

    // One week's published score for each player, keyed by email. Written by
    // publishScores to leaderboard/{season}/totalPoints/{email}/weeklyPoints/{week}
    // as { pickWinnerPoints, pickScorePoints, total, name }; a player with no
    // doc (week not published yet, or no picks that week) is left out.
    const getWeeklyPointsForWeek = useCallback(async (season, week, emails) => {
        try {
            const snaps = await Promise.all((emails || []).map((email) =>
                getDoc(doc(db, 'leaderboard', season, 'totalPoints', email, 'weeklyPoints', String(week)))
            ));
            const byEmail = {};
            snaps.forEach((snap, i) => {
                if (snap.exists()) byEmail[emails[i]] = snap.data();
            });
            return byEmail;
        } catch (error) {
            console.error('Error fetching weekly points:', error);
            return {};
        }
    }, []);

    // Every published week for one player, as [{ week, ...weeklyDoc }] sorted
    // by week number (doc ids are strings, so "10" would sort before "2").
    const getUserWeeklyPoints = useCallback(async (season, email) => {
        try {
            const snapshot = await getDocs(
                collection(db, 'leaderboard', season, 'totalPoints', email, 'weeklyPoints')
            );
            return snapshot.docs
                .map((d) => ({ week: Number(d.id), ...d.data() }))
                .filter((w) => !Number.isNaN(w.week))
                .sort((a, b) => a.week - b.week);
        } catch (error) {
            console.error('Error fetching weekly points for user:', error);
            return [];
        }
    }, []);

    const savePicks = useCallback(async (picks, season, week) => {
        try {
            // Write via the submitPicks Cloud Function, which enforces per-game
            // kickoff locking server-side. Direct Firestore writes to /picks are
            // denied by security rules.
            const submitPicks = httpsCallable(getFunctions(), 'submitPicks');
            return await submitPicks({ picks, season, week });
        } catch (error) {
            console.error('Error saving picks:', error);
            throw error; // Re-throw so the caller's error handler can show user feedback
        }
    }, []);

    const getAllSeasons = useCallback(async() => {
        try {
            const gamesRef = collection(db, 'games');
            const snapshot = await getDocs(gamesRef);
            return snapshot.docs.map(doc => doc.id);
        } catch (error) {
            console.error('Error fetching all seasons:', error);
            return [];
        }
    }, []);

    const value = {
        getGamesForWeek,
        getPicksForUser,
        getCurrentWeek,
        getLeaderboard,
        getWeeklyPointsForWeek,
        getUserWeeklyPoints,
        savePicks,
        getAllPicksForWeek,
        getAllSeasons,
        winningTeams,
        setWinningTeams,
        userDisplayNames,
        refreshUserDisplayNames,
        currentSeason,
        seasonLoading,
        setAppSeason,
        setCurrentWeek,
    };

    return (
        <DataContext.Provider value={value}>
            {!seasonLoading && children}
        </DataContext.Provider>
    )
}
